import cx from 'classnames'
import { RawButton } from '@/components/Button'
import {
  Dropdown,
  DropdownDivider,
  DropdownItem,
  DropdownItemDescription,
  DropdownItemTitle,
} from '@/components/Dropdown'
import { Icon } from '@/components/Icon'
import type {
  FilterTypeGroups,
  FilterTypeInfo,
} from '@/features/legends/filters/filter-types'
import type { FilterType } from '@/features/legends/filters/rules'
import styles from './AddFilterButton.module.css'

export interface AddFilterButtonProps {
  filterTypes: FilterTypeGroups
  onAdd: (type: FilterType) => unknown
  /**
   * Shows a full-width button with this label, for an empty slot, instead
   * of the compact "Add filter" button.
   */
  label?: string
  isDisabled?: boolean
  isInvalid?: boolean
  /**
   * The button's test ID; menu items get `${testId}-${type}`.
   */
  testId?: string
}

/**
 * Menu of the filter types that can be added: essentials first.
 */
export function AddFilterButton({
  filterTypes,
  onAdd,
  label,
  isDisabled,
  isInvalid,
  testId,
}: AddFilterButtonProps) {
  const renderType = (t: FilterTypeInfo) => (
    <DropdownItem
      key={t.type}
      onPress={() => onAdd(t.type)}
      testId={testId && `${testId}-${t.type}`}
    >
      <DropdownItemTitle>{t.friendlyName}</DropdownItemTitle>
      <DropdownItemDescription>{t.friendlyDescription}</DropdownItemDescription>
    </DropdownItem>
  )
  return (
    <div className={cx(styles.root, label && styles.slot)}>
      <Dropdown
        autoDismiss
        position="bottom-start"
        isDisabled={isDisabled}
        button={
          <RawButton
            className={cx(
              label ? styles.slotButton : styles.button,
              isInvalid && styles.invalid,
            )}
            isDisabled={isDisabled}
            testId={testId && `${testId}-button`}
          >
            <Icon name="add" size={14} aria-hidden="true" />
            {label ?? 'Add filter'}
          </RawButton>
        }
      >
        {filterTypes.essentials.map(renderType)}
        {filterTypes.others.length > 0 && <DropdownDivider />}
        {filterTypes.others.map(renderType)}
      </Dropdown>
    </div>
  )
}
